/**
 * Idle Timeout Hook
 *
 * Tracks user activity and triggers a callback after a period of inactivity.
 * Used for GDPR/HIPAA session management — automatically signs out idle users.
 *
 * Monitors: mouse, keyboard, touch, scroll, and visibility changes.
 * Does NOT monitor network activity (API calls don't count as user activity).
 *
 * Idle means the PERSON is idle, across every tab, not this tab. Activity is
 * published to localStorage, and a tab re-reads it before it warns or signs
 * out (no live listener: the re-read at the deadline is what decides).
 *
 * This matters because a sign-out in one tab signs out all of them (Firebase
 * syncs it through localStorage, see config/firebase.ts): with a per-tab
 * clock, an untouched background tab would sign the user out of the tab they
 * are working in once its 15/30 minutes ran out.
 *
 * @compliance GDPR — session security
 * @compliance HIPAA 164.312(a)(2)(iii) — automatic logoff
 */

import { useEffect, useRef, useCallback } from 'react';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'pointermove'] as const;

/** localStorage key holding the epoch ms of the latest activity in ANY tab. */
export const IDLE_ACTIVITY_KEY = 'bower.idle.lastActivityAt';

/**
 * Minimum gap between two writes of the shared timestamp from one tab, so
 * `pointermove` does not write localStorage on every frame. Small next to the
 * 15-minute minimum timeout, so the shared clock is never meaningfully stale.
 */
const PUBLISH_THROTTLE_MS = 5_000;
let lastPublishedAt = 0;

/** Record activity for every tab. Never throws: storage may be unavailable. */
export function publishIdleActivity(now: number = Date.now()): void {
  // abs(): a clock stepped backwards must not stall publishing until it catches up.
  if (Math.abs(now - lastPublishedAt) < PUBLISH_THROTTLE_MS) return;
  lastPublishedAt = now;
  try {
    localStorage.setItem(IDLE_ACTIVITY_KEY, String(now));
  } catch {
    // No shared clock; this tab still keeps its own.
  }
}

function readSharedActivity(): number {
  try {
    const value = Number(localStorage.getItem(IDLE_ACTIVITY_KEY));
    // A timestamp later than now (a clock stepped backwards, a hand-written
    // value) is not evidence of activity. Trusting it would both extend an idle
    // person's window and mask real activity written after it. Ignore it rather
    // than clamping `since` to now, which would hold the session open forever.
    return Number.isFinite(value) && value <= Date.now() ? value : 0;
  } catch {
    return 0;
  }
}

/**
 * Maximum delay for a single `setTimeout` call.
 * The HTML spec allows browsers to clamp delays at 2^31 - 1 ms (~24.8 days).
 * Values above this overflow the 32-bit signed integer and fire immediately.
 */
const MAX_TIMEOUT_MS = 2 ** 31 - 1; // 2 147 483 647 ms ≈ 24.8 days

interface UseIdleTimeoutOptions {
  /** Timeout duration in milliseconds. Default: 30 minutes. */
  timeoutMs?: number;
  /** Milliseconds before timeout to show a warning. Default: 2 minutes before. */
  warningMs?: number;
  /** Called when the warning threshold is reached. */
  onWarning?: () => void;
  /** Called when the timeout expires. */
  onTimeout: () => void;
  /** Whether the timeout is active. Default: true. */
  enabled?: boolean;
  /**
   * Called when activity in ANOTHER tab restarts this tab's clock, so a warning
   * already showing here can be dismissed: the person is not idle.
   */
  onActivityElsewhere?: () => void;
}

/**
 * Hook that tracks user idle time and triggers callbacks.
 *
 * Returns `resetTimer()` to manually reset the idle clock (e.g., after
 * dismissing a warning dialog).
 */
export function useIdleTimeout({
  timeoutMs = 30 * 60 * 1000, // 30 minutes
  warningMs = 2 * 60 * 1000, // 2 minutes before timeout
  onWarning,
  onTimeout,
  enabled = true,
  onActivityElsewhere,
}: UseIdleTimeoutOptions): { resetTimer: () => void } {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningFiredRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    timeoutRef.current = null;
    warningRef.current = null;
  }, []);

  // Arms both timers for an idle window that began at `since`. Each one
  // re-reads the shared clock when it fires: if any tab was active after
  // `since`, the window restarts from that activity instead of warning or
  // signing out. A re-read rather than a `storage` listener because it holds
  // when a background tab's events are throttled or missed, and a warning
  // already open here still closes by the deadline (onActivityElsewhere).
  const startTimers = useCallback(
    function arm(since: number = Date.now()) {
      clearTimers();
      warningFiredRef.current = false;

      // Clamp delay to 32-bit signed integer max to prevent setTimeout overflow.
      // Values > 2^31-1 wrap around and fire immediately in real browsers.
      const safeTimeout = Math.min(timeoutMs, MAX_TIMEOUT_MS);
      const elapsed = Math.max(0, Date.now() - since);
      const activeElsewhereSince = () => {
        const shared = readSharedActivity();
        return shared > since ? shared : null;
      };

      // Warning timer
      if (onWarning && warningMs < timeoutMs) {
        warningRef.current = setTimeout(
          () => {
            const shared = activeElsewhereSince();
            if (shared !== null) return arm(shared);
            warningFiredRef.current = true;
            onWarning();
          },
          Math.max(0, safeTimeout - warningMs - elapsed),
        );
      }

      // Timeout timer
      timeoutRef.current = setTimeout(
        () => {
          const shared = activeElsewhereSince();
          if (shared !== null) {
            onActivityElsewhere?.();
            return arm(shared);
          }
          onTimeout();
        },
        Math.max(0, safeTimeout - elapsed),
      );
    },
    [clearTimers, timeoutMs, warningMs, onWarning, onTimeout, onActivityElsewhere],
  );

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    publishIdleActivity();
    startTimers();
  }, [enabled, startTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    // Start initial timer. A page load is activity: it is what a person does.
    publishIdleActivity();
    startTimers();

    // Reset on user activity
    const handleActivity = () => {
      // Only reset if warning hasn't fired yet (once warned, let the timer run)
      if (!warningFiredRef.current) {
        publishIdleActivity();
        startTimers();
      }
    };

    // Pause timer when tab is hidden, resume when visible
    const handleVisibility = () => {
      if (document.hidden) {
        // Tab hidden — keep timers running (user is idle)
      } else {
        // Tab visible again — this counts as activity
        if (!warningFiredRef.current) {
          publishIdleActivity();
          startTimers();
        }
      }
    };

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, handleActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimers();
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, handleActivity);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, startTimers, clearTimers]);

  return { resetTimer };
}
